/**
 * @fileoverview Connect authorized banking detail reveal to revisioned staff edits and verification/delegation actions. The backend owns sensitive fields, current revision, and action admission; this view owns displayed/draft state and stable operation identity. `PortalResult.js` supplies review context. Revealing numbers, editing them, and confirming a specific version are distinct operations.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppButton, AppIcon } from '../shared';
import './BankingDetails.css';
import VerificationWorkflow from './VerificationWorkflow';
import ExistingBankingDetails from './ExistingBankingDetails';

 
function GuidedBankingDetails({ reviewId, result, matterNumber, clientId, onUpdated, onReload }) {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(false);
  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const generation = useRef(0);
  const operation = useRef(null);
  const context = matterNumber && clientId ? { matter_number: matterNumber, client_id: Number(clientId) } : {};
  const destination = result.submission.destination;
  const status = result.banking;

   
  async function load() {
    const current = generation.current;
    setLoading(true); setError(''); setBank(null);
    try {
      const { data } = await api.post(`/payment-instructions/${encodeURIComponent(reviewId)}/reveal`, {
        revision: result.revision, ...context,
      });
      if (generation.current === current) setBank(data.destination);
    } catch (failure) {
      if (generation.current === current) setError(failure.response?.data?.error || 'Bank details could not be loaded.');
    } finally { if (generation.current === current) setLoading(false); }
  }
  useEffect(() => {
    generation.current += 1;
    if (result.can_reveal) load();
    return () => { generation.current += 1; };
    // A saved revision is already returned with its matching full bank details.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, matterNumber, clientId]);

   
  async function act(action, method, evidence = {}) {
    const current = generation.current;
    const payload = { action, revision: result.revision, banking_revision: bank.staffRevision || 0,
      ...context, ...evidence, ...(action === 'save' ? { routing_number: routing, account_number: account } : { method }) };
    const key = JSON.stringify(payload);
    if (operation.current?.key !== key) operation.current = { key, id: crypto.randomUUID() };
    setBusy(true); setError(''); setNotice('');
    try {
      const { data } = await api.put(`/payment-instructions/${encodeURIComponent(reviewId)}/banking`, {
        ...payload, operation_id: operation.current.id,
      });
      if (generation.current !== current) return;
      setBank(data.destination); setEditing(false); operation.current = null;
      setNotice(action === 'save' ? 'Changes saved' : 'Banking details verified');
      onUpdated(data);
    } catch (failure) {
      if (generation.current === current) setError(failure.response?.data?.error || 'Banking details could not be saved.');
    } finally { if (generation.current === current) setBusy(false); }
  }

   
  async function copy(value, label) {
    try { await navigator.clipboard.writeText(value); setNotice(`${label} copied`); }
    catch { setError('Copying was blocked. Select the number to copy it.'); }
  }

   
  const visible = result.can_reveal && bank;
  const confirmed = status?.confirmed && bank && (status.banking_revision ?? 0) === (bank.staffRevision || 0)
    && (!status.version_id || status.version_id === bank.staffVersionId);
  return <section className="banking-details" aria-label="Banking details">
    <header className="banking-details__header">
      <h3><AppIcon icon="pi pi-wallet" aria-hidden="true" /> Banking details</h3>
      {confirmed ? <span className="banking-details__verified"><AppIcon icon="pi pi-check-circle" aria-hidden="true" />
        {status.method === 'stripe' ? 'Verified · Stripe only' : status.method === 'video' ? 'Video verified' : 'Phone verified'}</span>
        : result.can_reveal ? <span className="banking-details__pending"><AppIcon icon="pi pi-clock" aria-hidden="true" /> Awaiting staff acceptance</span> : null}
    </header>
    <p className="banking-details__bank">{destination.bank_name} · {destination.account_type}</p>
    <p>Account holder: {destination.account_holder}</p>
    {loading ? <p role="status">Loading bank details…</p> : null}
    {visible ? <>
      <div className="banking-details__numbers">
        <label>Routing number
          <span className="banking-details__number">
            {editing ? <input aria-label="Routing number" inputMode="numeric" autoComplete="off" value={routing}
              maxLength={9} onChange={event => setRouting(event.target.value)} disabled={busy} /> : <strong>{bank.routingNumber}</strong>}
            {!editing ? <button type="button" title="Copy routing number" aria-label="Copy routing number" onClick={() => copy(bank.routingNumber, 'Routing number')}><AppIcon icon="pi pi-copy" aria-hidden="true" /></button> : null}
          </span>
        </label>
        <label>Account number
          <span className="banking-details__number">
            {editing ? <input aria-label="Account number" inputMode="numeric" autoComplete="off" value={account}
              maxLength={17} onChange={event => setAccount(event.target.value)} disabled={busy} /> : <strong>{bank.accountNumber}</strong>}
            {!editing ? <button type="button" title="Copy account number" aria-label="Copy account number" onClick={() => copy(bank.accountNumber, 'Account number')}><AppIcon icon="pi pi-copy" aria-hidden="true" /></button> : null}
          </span>
        </label>
      </div>
      <div className="banking-details__actions">
        {editing ? <>
          <AppButton disabled={busy || !/^[0-9]{9}$/.test(routing) || !/^[0-9]{4,17}$/.test(account)} icon="pi pi-check" onClick={() => act('save')}>Save changes</AppButton>
          <AppButton variant="secondary" disabled={busy} onClick={() => setEditing(false)}>Cancel</AppButton>
        </> : <>
          <AppButton variant="secondary" icon="pi pi-pencil" disabled={busy} onClick={() => { setRouting(bank.routingNumber); setAccount(bank.accountNumber); setEditing(true); setNotice(''); }}>Edit details</AppButton>
        </>}
      </div>
      {!editing ? <VerificationWorkflow reviewId={reviewId} result={result} bank={bank} confirmed={confirmed}
        busy={busy} onConfirm={(method, evidence) => act('confirm', method, evidence)} onUpdated={onUpdated}
        onReload={onReload} matterNumber={matterNumber} clientId={clientId} /> : null}
    </> : !loading ? <p>Account ending {status?.account_last4 || destination.account_last4}</p> : null}
    {status?.recorded_by && (!status.confirmed || confirmed) ? <p className="banking-details__attribution">{status.confirmed ? 'Confirmed' : 'Updated'} by {status.recorded_by} · {new Date(status.recorded_at).toLocaleString()}</p> : null}
    {status?.delegate?.status === 'open' ? <p className="banking-details__attribution">Verification assigned to {status.delegate.name}</p> : null}
    {error ? <div role="alert"><p>{error}</p>{result.can_reveal ? <AppButton variant="secondary" icon="pi pi-refresh" onClick={onReload || load}>Reload details</AppButton> : null}</div> : null}
    {notice ? <p role="status">{notice}</p> : null}
  </section>;
}

export default function BankingDetails(props) {
  return props.result.workflow?.enabled === false
    ? <ExistingBankingDetails {...props} /> : <GuidedBankingDetails {...props} />;
}
